import { useEffect, useRef, useState } from 'react';
import { Bell } from 'lucide-react';

import { useSession } from '../../app/useSession';
import { cn } from '../../lib/cn';
import { formatHebrewRelative } from '../../lib/relativeTime';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
  type NotificationRow,
} from './notificationsQueries';
import styles from './TopBar.module.css';

const KIND_LABELS: Record<string, string> = {
  lead_new: 'ליד חדש',
  lead_assigned: 'הוקצה ליד',
  deal_stuck: 'דיל תקוע',
  deal_won: 'דיל נסגר',
  invoice_paid: 'חשבונית שולמה',
  invoice_overdue: 'חשבונית באיחור',
  quote_viewed: 'הצעת מחיר נצפתה',
  task_due: 'משימה ממתינה',
  mention: 'אוזכרת',
  comment: 'תגובה חדשה',
  meeting_soon: 'פגישה קרובה',
  whatsapp_inbound: 'הודעת WhatsApp',
  system_update: 'עדכון מערכת',
  error: 'שגיאה',
};

function NotificationItem({
  row,
  onMarkRead,
}: {
  row: NotificationRow;
  onMarkRead: (id: string) => void;
}) {
  const isUnread = !row.read_at;
  return (
    <li className={cn(styles.notifItem, isUnread && styles.notifUnread)}>
      <button
        type="button"
        className={styles.notifBody}
        onClick={() => {
          if (isUnread) onMarkRead(row.id);
        }}
      >
        <span className={styles.notifKind}>{KIND_LABELS[row.kind] ?? row.kind}</span>
        <span className={styles.notifTitle}>{row.title}</span>
        {row.body ? <span className={styles.notifText}>{row.body}</span> : null}
        <span className={styles.notifTime}>{formatHebrewRelative(row.created_at)}</span>
      </button>
    </li>
  );
}

export function NotificationBell() {
  const session = useSession();
  const sessionObj = session.status === 'signed-in' ? session.session : null;
  const userId = sessionObj?.user.id ?? null;
  const { data: notifications = [] } = useNotifications(userId);
  const markRead = useMarkNotificationRead(userId);
  const markAllRead = useMarkAllNotificationsRead(userId);
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const unreadCount = notifications.filter((n) => !n.read_at).length;

  useEffect(() => {
    if (!open) return;
    function onClick(event: MouseEvent) {
      const target = event.target as Node;
      if (popoverRef.current?.contains(target)) return;
      if (buttonRef.current?.contains(target)) return;
      setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    window.addEventListener('mousedown', onClick);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onClick);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className={styles.notifWrap}>
      <button
        ref={buttonRef}
        type="button"
        className={styles.iconBtn}
        aria-label={`התראות (${String(unreadCount)})`}
        aria-expanded={open}
        onClick={() => {
          setOpen((o) => !o);
        }}
      >
        <Bell size={18} />
        {unreadCount > 0 ? (
          <span className={styles.bellBadge} aria-hidden="true">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        ) : null}
      </button>

      {open ? (
        <div ref={popoverRef} className={styles.notifPopover} role="dialog" aria-label="התראות">
          <header className={styles.notifHead}>
            <span>התראות</span>
            {unreadCount > 0 ? (
              <button
                type="button"
                className={styles.notifMarkAll}
                onClick={() => {
                  markAllRead.mutate();
                }}
              >
                סמן הכל כנקרא
              </button>
            ) : null}
          </header>

          {notifications.length === 0 ? (
            <p className={styles.notifEmpty}>אין התראות.</p>
          ) : (
            <ul className={styles.notifList}>
              {notifications.map((row) => (
                <NotificationItem
                  key={row.id}
                  row={row}
                  onMarkRead={(id) => {
                    markRead.mutate(id);
                  }}
                />
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}
